

import React from 'react'
import { createRoot } from 'react-dom/client'
import cuid from 'cuid'
import Cookie from 'js-cookie'
import getFingerprint from 'shared/utils/get_fingerprint'

import LocaleActions from 'store/v2/redux/actions/locale'
import clientActions from 'store/v2/redux/actions/client'
import ItemActions from 'store/v2/redux/actions/item'
import BundleActions from 'store/v2/redux/actions/bundle'
import ProductGroupActions from 'store/v2/redux/actions/product_group'
import getProvider from 'store/v2/get_provider'
import { initShopTracking } from 'shared/utils/userFrameworkEvents'

import Cart from './scenes/cart'
import { ProductGroup } from './scenes/product_group'
import { List as ProductList } from './scenes/product_list'
import ProductButton from 'store/v2/react/scenes/product_button'
import ProductDetail from './scenes/product_detail'
import Quickview from './scenes/quickview'
import ProductSearch from './scenes/product_search'
import SeparateDatePicker from './scenes/separate_date_picker'
import CollectionList from './scenes/collection_list'
import FilterBar from './scenes/filter_bar'
import SortingSelect from './scenes/sorting_select'
import Sidebar from './scenes/sidebar'
import EmbeddableCart from './scenes/embeddable_cart'
import EmbeddableCartLines from './scenes/embeddable_cart_lines'
import EmbeddableCartSidebar from './scenes/embeddable_cart_sidebar'
import CartButton from './scenes/cart_button'
import EmbeddableCartPicker from 'store/modules/cart/CartPicker/Embeddable'

import 'shop-components/components/ProductAvailability'
import 'shop-components/components/ProductPrice/Price'
import 'shop-components/components/ProductPrice/Label'
import 'shop-components/components/ProductVariationsSelect'
import 'shop-components/components/ProductAvailabilityCalendar'
import 'shop-components/components/ProductButton'
import 'shop-components/components/DatePicker'
import 'shop-components/components/DatePicker/Modal'
import 'shop-components/components/MiniCart'
import 'shop-components/components/ProductGallery'
import 'shop-components/components/BundleItems'
import 'shop-components/components/CartLines'
import 'shop-components/components/CartServices'
import 'shop-components/components/CartTotals'
import 'shop-components/components/MiniCartLauncher'
import 'shop-components/components/MiniCartButton'

// Set storeProvider for tracking
if (!window.booqableOptions.storeProvider) {
  if (getProvider.isWix()) {
    window.booqableOptions.storeProvider = 'Wix'
  } else if (getProvider.isWeebly()) {
    window.booqableOptions.storeProvider = 'Weebly'
  } else if (getProvider.isSquarespace()) {
    window.booqableOptions.storeProvider = 'Squarespace'
  } else if (getProvider.isShopify()) {
    window.booqableOptions.storeProvider = 'Shopify'
  } else {
    window.booqableOptions.storeProvider = 'Custom'
  }
}

let cartId

// Migrate 'old' cookie to localStorage
if ((cartId = Cookie.get('cartId'))) {
  localStorage.bqCartId = cartId
  Cookie.remove('cartId')
}

if ((cartId = localStorage.bqCartId)) {
  clientActions.setCartId(cartId)
}

const booqableRoots = []

// memoize root elements
let cartRoot, quickviewRoot

window.initBooqableComponents = function () {
  for (const root of Array.from(booqableRoots)) {
    root?.unmount()
  }

  // For fetching product card data
  const cardIds = []

  // For fetching button/detail data
  const detailIds = []

  const initializeLocale = () => {
    let locale

    if (navigator.language != null) {
      locale = navigator.language
    } else if (navigator.languages != null) {
      locale = navigator.languages[0]
    }

    return LocaleActions.set(locale)
  }

  const initializeLauncher = () => {
    if (window.booqableOptions.disableLauncher) {
      return
    }

    const embeddableCartElements = {
      'booqable-cart-button': document.querySelector('.booqable-cart-button'),
      'booqable-embeddable-cart': document.querySelector('.booqable-embeddable-cart'),
      'booqable-embeddable-cart-lines': document.querySelector('.booqable-embeddable-cart-lines'),
      'booqable-embeddable-cart-sidebar': document.querySelector('.booqable-embeddable-cart-sidebar'),
      'bq-minicart': document.querySelector('bq-minicart'),
      'bq-minicart-launcher': document.querySelector('bq-minicart-launcher'),
      'bq-minicart-button': document.querySelector('bq-minicart-button'),
      'bq-date-picker': document.querySelector('bq-date-picker'),
      'bq-date-picker-modal': document.querySelector('bq-date-picker-modal')
    }

    const usedEmbeddableCartElements = Object.entries(embeddableCartElements).filter(([_, v]) => v !== null)

    if (usedEmbeddableCartElements.length > 0) {
      // We save the embeddableCartElements key to the booqableOptions objects for tracking purposes
      window.booqableOptions.embeddableCartElements = Object.keys(
        Object.fromEntries(usedEmbeddableCartElements)
      ).join(', ')

      const embeddableTargetPrefix = 'booqable-embeddable-cart'
      const embeddableTargetElementsExist = usedEmbeddableCartElements.some(([key]) => key.startsWith(embeddableTargetPrefix))
      const nonTargetElementsExist = usedEmbeddableCartElements.some(([key]) => !key.startsWith(embeddableTargetPrefix))

      if (embeddableTargetElementsExist && !nonTargetElementsExist) {
        // We have an embeddable cart elements, but the picker is not mounted
        // Mounting the picker without mounting the floating cart
        const root = createRoot(document.createElement('div'))

        root.render(<EmbeddableCartPicker />)
      }

      // Embeddable cart is being used, don't use launcher
      return
    }

    // # Initialize the cart launcher
    let launcher

    if (!(launcher = document.getElementById('booqable-cart'))) {
      launcher = document.createElement('div')
      launcher.setAttribute('id', 'booqable-cart')
      document.body.appendChild(launcher)
      clientActions.setFloatingCart()
    }

    const component = <Cart />

    cartRoot = cartRoot || createRoot(document.getElementById('booqable-cart'))
    cartRoot.render(component)

    return launcher.setAttribute('initialized', 'true')
  }

  const initializeProductQuickview = () => {
    // # Initialize quickview
    const quickview = document.createElement('div')

    quickview.setAttribute('id', 'booqable-quickview')
    document.body.appendChild(quickview)

    const component = <Quickview />

    quickviewRoot = quickviewRoot || createRoot(document.getElementById('booqable-quickview'))

    return quickviewRoot.render(component)
  }

  const initializeLegacy = () => {
    // # Support for legacy v1 components
    const legacyComponents = document.getElementsByClassName('booqable-product-component')

    return Array.from(legacyComponents).map((node) => (node.className += ' booqable-product-button'))
  }

  const initializeProducts = () => {
    const products = document.getElementsByClassName('booqable-product')

    const result = []

    for (const node of Array.from(products)) {
      if (node.parentElement.className !== 'booqable-product-list') {
        // Get product group id
        const { id } = node.dataset

        cardIds.push(id)

        const component = <ProductGroup {...node.dataset} />
        const root = createRoot(node)

        root.render(component)
        result.push(booqableRoots.push(root))
      } else {
        result.push(undefined)
      }
    }

    return result
  }

  const initializeProductButtons = () => {
    // # Render all products buttons
    const productButtons = document.getElementsByClassName('booqable-product-button')

    const result = []

    for (const node of Array.from(productButtons)) {
      const { dataset } = node
      // Get product group id
      const { id } = dataset

      dataset.uiKey = id
      dataset.productGroupId = id
      detailIds.push(id)

      const component = <ProductButton {...dataset} />
      const root = createRoot(node)

      root.render(component)
      result.push(booqableRoots.push(root))
    }

    return result
  }

  const initializeProductDetails = () => {
    // # Render all product details
    const productDetails = document.getElementsByClassName('booqable-product-detail')

    const result = []

    for (const node of Array.from(productDetails)) {
      const { dataset } = node
      // Get product group id
      const { id } = dataset

      dataset.uiKey = id
      dataset.productGroupId = id
      detailIds.push(id)

      const component = <ProductDetail {...dataset} />
      const root = createRoot(node)

      root.render(component)
      result.push(booqableRoots.push(root))
    }

    return result
  }

  const initializeProductLists = () => {
    // # Render Product List component
    const productLists = document.getElementsByClassName('booqable-product-list')

    const result = []

    for (const node of Array.from(productLists)) {
      const props = node.dataset

      props.uiKey = cuid()

      const component = <ProductList {...props} />
      const root = createRoot(node)

      root.render(component)
      result.push(booqableRoots.push(root))
    }

    return result
  }

  const initializeProductSearch = () => {
    // # Render product search component
    const productSearches = document.getElementsByClassName('booqable-product-search')

    const result = []

    for (const node of Array.from(productSearches)) {
      const props = node.dataset

      props.uiKey = cuid()

      const component = <ProductSearch {...props} />
      const root = createRoot(node)

      root.render(component)
      result.push(booqableRoots.push(root))
    }

    return result
  }

  const initializeStandardComponents = () => {
    const result = []
    const components = {
      'booqable-datepicker': SeparateDatePicker,
      'booqable-categories': CollectionList,
      'booqable-collections': CollectionList,
      'booqable-bar': FilterBar,
      'booqable-sort': SortingSelect,
      // Re-enable when availability filtering performance is improved
      // 'booqable-filter': FilterSelect,
      'booqable-sidebar': Sidebar,
      'booqable-embeddable-cart': EmbeddableCart,
      'booqable-embeddable-cart-lines': EmbeddableCartLines,
      'booqable-embeddable-cart-sidebar': EmbeddableCartSidebar,
      'booqable-cart-button': CartButton
    }

    Object.keys(components).forEach((className) => {
      const nodes = document.getElementsByClassName(className)

      for (const node of Array.from(nodes)) {
        const props = node.dataset
        const Component = components[className]
        const root = createRoot(node)

        root.render(<Component {...props} />)
        result.push(booqableRoots.push(root))
      }
    })

    return result
  }

  initializeLocale()
  initializeLegacy()
  initializeProducts()
  initializeProductButtons()
  initializeProductDetails()
  initializeProductLists()
  initializeProductSearch()
  initializeStandardComponents()

  const initializeBodyDependentElements = () => {
    initializeLauncher()
    initializeProductQuickview()
  }

  // Initialize immediately if body is already present.
  // Otherwise, wait for the body to be available.
  if (document.body) {
    initializeBodyDependentElements()
  } else {
    const observer = new MutationObserver(() => {
      if (document.body) {
        initializeBodyDependentElements()
        observer.disconnect()
      }
    })
    observer.observe(document, {
      childList: true,
      subtree: true
    })
  }

  // # Fetch all product groups on the page
  if (cardIds.length > 0) {
    if (!window.bqFetchingProducts) {
      window.bqFetchingProducts = true

      ItemActions.fetchAll({ ids: cardIds, cart_id: cartId, include: 'photo', apiVersion: 3 })
        .then(() => {
          window.bqFetchingProducts = false
        }).catch((e) => {
          window.bqFetchingProducts = false

          if (e === 'Online reservations are disabled') {
            booqableRoots.forEach((root) => root?.unmount())

            cartRoot = cartRoot || createRoot(document.getElementById('booqable-cart'))
            cartRoot.unmount()
          }
        })
    }
  }

  if (detailIds.length > 0) {
    window.bqFetchingDetailProducts = true

    const actions = []

    actions.push(ProductGroupActions.fetchAll({
      ids: detailIds,
      cart_id: cartId,
      include: 'photos,products',
      apiVersion: 3
    }))

    actions.push(BundleActions.fetchAll({
      ids: detailIds,
      cart_id: cartId,
      include: [
        'photos',
        'bundle_items',
        'bundle_items.product',
        'bundle_items.product.photo',
        'bundle_items.product_group',
        'bundle_items.product_group.products',
        'bundle_items.product_group.photo',
        'bundle_items.product_group.products',
        'bundle_items.product_group.products.photo'
      ].join(','),
      apiVersion: 3
    }))

    Promise.all(actions)
      .then(() => {
        window.bqFetchingDetailProducts = false
      })
      .catch(() => {
        window.bqFetchingDetailProducts = false
      })
  }
}

getFingerprint((fingerprint) => {
  window.fingerprint = fingerprint
  window.initBooqableComponents()

  clientActions.initialize().then(() => {
    initShopTracking()
  })
})
